import React, { useState, useEffect } from 'react';
import {
  Upload,
  Camera,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  MapPin,
  User,
  Image as ImageIcon,
  Zap,
  Eye,
  Trash2,
  Search,
  Filter,
  Phone,
  Edit,
  X
} from 'lucide-react';

const AdminACUnitsAuto = ({ appointments }) => {
  const [acUnits, setACUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [processingResult, setProcessingResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [customerMatches, setCustomerMatches] = useState([]);
  const [pendingUnitId, setPendingUnitId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    model_number: '',
    manufacturer: '',
    tonnage: '',
    btu: '',
    seer: '',
    unit_type: '',
    height_inches: '',
    width_inches: '',
    depth_inches: '',
    customer_id: '',
    appointment_id: '',
    notes: '',
    image: null,
    imagePreview: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_BASE = '/scheduler/api';

  useEffect(() => {
    loadACUnits();
  }, []);

  const loadACUnits = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/ac-units.php`);
      const result = await response.json();
      if (result.success) {
        setACUnits(result.data || []);
      }
    } catch (error) {
      console.error('Error loading AC units:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be less than 10MB');
      return;
    }

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select an image first');
      return;
    }

    setProcessing(true);
    setProcessingResult(null);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('image', selectedFile);

      // Upload and process
      const response = await fetch(`${API_BASE}/ac-units-upload.php`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (result.success) {
        // Check if multiple customers were found
        if (result.requires_customer_selection) {
          // Show customer selector modal
          setCustomerMatches(result.data.customer_matches || []);
          setPendingUnitId(result.unit_id);
          setShowCustomerSelector(true);
          setProcessingResult({
            success: true,
            message: result.message + ' - ' + result.data.match_count + ' matches found',
            data: result.data
          });
        } else {
          // Single or no match - processed normally
          setProcessingResult({
            success: true,
            message: 'AC unit processed successfully!',
            data: result.data
          });

          // Clear form after 3 seconds
          setTimeout(() => {
            setSelectedFile(null);
            setPreview(null);
            setProcessingResult(null);
          }, 5000);
        }

        // Reload AC units list
        loadACUnits();
      } else {
        setProcessingResult({
          success: false,
          message: 'Processing failed: ' + (result.error || 'Unknown error'),
          data: null
        });
      }
    } catch (error) {
      setProcessingResult({
        success: false,
        message: 'Upload failed: ' + error.message,
        data: null
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCustomerSelection = async (customerId) => {
    try {
      const response = await fetch(`${API_BASE}/ac-unit-assign-customer.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          unit_id: pendingUnitId,
          customer_id: customerId
        })
      });

      const result = await response.json();

      if (result.success) {
        // Close modal
        setShowCustomerSelector(false);
        setCustomerMatches([]);
        setPendingUnitId(null);

        // Reload AC units
        loadACUnits();

        // Clear form
        setTimeout(() => {
          setSelectedFile(null);
          setPreview(null);
          setProcessingResult(null);
        }, 2000);
      }
    } catch (error) {
      // Error handling
    }
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm('Are you sure you want to delete this AC unit?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/ac-units.php?id=${unitId}`, {
        method: 'DELETE'
      });

      const result = await response.json();
      if (result.success) {
        loadACUnits();
      }
    } catch (error) {
      // Error deleting unit
    }
  };

  const resetEditForm = () => {
    setEditForm({
      id: '',
      model_number: '',
      manufacturer: '',
      tonnage: '',
      btu: '',
      seer: '',
      unit_type: '',
      height_inches: '',
      width_inches: '',
      depth_inches: '',
      customer_id: '',
      appointment_id: '',
      notes: '',
      image: null,
      imagePreview: null
    });
  };

  const openEditModal = (unit) => {
    setEditForm({
      id: unit.id,
      model_number: unit.model_number || '',
      manufacturer: unit.manufacturer || '',
      tonnage: unit.tonnage || '',
      btu: unit.btu || '',
      seer: unit.seer || '',
      unit_type: unit.unit_type || '',
      height_inches: unit.height_inches || '',
      width_inches: unit.width_inches || '',
      depth_inches: unit.depth_inches || '',
      customer_id: unit.customer_id || '',
      appointment_id: unit.appointment_id || '',
      notes: unit.notes || '',
      image: null,
      imagePreview: unit.image_path || null
    });
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Image must be less than 10MB');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditForm(prev => ({
          ...prev,
          image: file,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const acUnitData = {
        id: editForm.id,
        model_number: editForm.model_number,
        manufacturer: editForm.manufacturer || null,
        tonnage: editForm.tonnage ? parseFloat(editForm.tonnage) : null,
        btu: editForm.btu ? parseInt(editForm.btu) : null,
        seer: editForm.seer ? parseInt(editForm.seer) : null,
        unit_type: editForm.unit_type || null,
        height_inches: editForm.height_inches ? parseFloat(editForm.height_inches) : null,
        width_inches: editForm.width_inches ? parseFloat(editForm.width_inches) : null,
        depth_inches: editForm.depth_inches ? parseFloat(editForm.depth_inches) : null,
        customer_id: editForm.customer_id || null,
        appointment_id: editForm.appointment_id || null,
        notes: editForm.notes || null,
        image_path: editForm.imagePreview || '/placeholder-ac-unit.jpg'
      };

      const response = await fetch(`${API_BASE}/ac-units.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(acUnitData)
      });

      const result = await response.json();

      if (result.success) {
        alert('AC Unit updated successfully!');
        setShowEditModal(false);
        resetEditForm();
        loadACUnits();
      } else {
        alert('Failed to update AC unit: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating AC unit:', error);
      alert('Error updating AC unit: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-blue-600 animate-spin" />;
      case 'pending':
        return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter AC units
  const filteredUnits = acUnits.filter(unit => {
    const matchesSearch =
      (unit.model_number && unit.model_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (unit.manufacturer && unit.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (unit.customer_name && unit.customer_name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = filterStatus === 'all' || unit.processing_status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* AI-Powered Upload Section */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Zap className="w-8 h-8" />
          <div>
            <h3 className="text-2xl font-bold">AI-Powered AC Unit Scanner</h3>
            <p className="text-green-100">Upload a photo and let AI do the rest</p>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur rounded-lg p-4 mb-4">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <Camera className="w-5 h-5" />
            How it works:
          </h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-green-50">
            <li>Take a photo of the AC unit label</li>
            <li>AI extracts the model number from the photo</li>
            <li>System searches the web for unit dimensions (height & width)</li>
            <li>GPS coordinates are pulled from photo metadata</li>
            <li>Customer is auto-matched based on GPS location</li>
          </ol>
        </div>

        {/* Upload Interface */}
        <div className="bg-white rounded-lg p-6">
          <div className="space-y-4">
            {/* File Input */}
            <div>
              <label
                htmlFor="ac-photo"
                className="block w-full cursor-pointer"
              >
                <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  preview ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-500 hover:bg-gray-50'
                }`}>
                  {preview ? (
                    <div className="space-y-4">
                      <img
                        src={preview}
                        alt="Preview"
                        className="max-h-64 mx-auto rounded-lg"
                      />
                      <p className="text-sm text-gray-600">
                        {selectedFile?.name} ({(selectedFile?.size / 1024 / 1024).toFixed(2)} MB)
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-lg font-medium text-gray-700">
                        Click to upload AC unit photo
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        JPG, PNG, or HEIC up to 10MB
                      </p>
                    </div>
                  )}
                </div>
                <input
                  id="ac-photo"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={processing}
                />
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleUpload}
                disabled={!selectedFile || processing}
                className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-all"
              >
                {processing ? (
                  <>
                    <Clock className="w-5 h-5 animate-spin" />
                    Processing with AI...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Process Photo
                  </>
                )}
              </button>
              {selectedFile && !processing && (
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreview(null);
                    setProcessingResult(null);
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold py-3 px-6 rounded-lg"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Processing Result */}
            {processingResult && (
              <div className={`p-4 rounded-lg ${
                processingResult.success
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {processingResult.success ? (
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className={`font-semibold ${
                      processingResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {processingResult.message}
                    </h4>

                    {processingResult.success && processingResult.data && (
                      <div className="mt-3 space-y-2 text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-white rounded p-2">
                            <span className="text-gray-600">Model:</span>
                            <span className="ml-2 font-medium text-gray-800">
                              {processingResult.data.model_number}
                            </span>
                          </div>
                          {processingResult.data.dimensions && (
                            <>
                              <div className="bg-white rounded p-2">
                                <span className="text-gray-600">Height:</span>
                                <span className="ml-2 font-medium text-gray-800">
                                  {processingResult.data.dimensions.height_inches}"
                                </span>
                              </div>
                              <div className="bg-white rounded p-2">
                                <span className="text-gray-600">Width:</span>
                                <span className="ml-2 font-medium text-gray-800">
                                  {processingResult.data.dimensions.width_inches}"
                                </span>
                              </div>
                            </>
                          )}
                          {processingResult.data.gps && processingResult.data.gps.latitude && (
                            <div className="bg-white rounded p-2 col-span-2">
                              <MapPin className="w-4 h-4 inline text-gray-600" />
                              <span className="ml-2 text-xs text-gray-600">
                                GPS: {processingResult.data.gps.latitude.toFixed(6)}, {processingResult.data.gps.longitude.toFixed(6)}
                              </span>
                            </div>
                          )}
                          {processingResult.data.customer_note && (
                            <div className="bg-blue-50 rounded p-2 col-span-2">
                              <span className="text-xs text-blue-800">
                                {processingResult.data.customer_note}
                              </span>
                            </div>
                          )}
                          {processingResult.data.match_count !== undefined && (
                            <div className="bg-white rounded p-2 col-span-2">
                              <span className="text-gray-600">Customers found:</span>
                              <span className="ml-2 font-medium text-gray-800">
                                {processingResult.data.match_count}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AC Units List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">
            AC Units Database ({acUnits.length})
          </h3>
        </div>

        {/* Search and Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by model, manufacturer, or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div className="relative">
            <Filter className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="processing">Processing</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading AC units...</p>
          </div>
        ) : filteredUnits.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No AC Units Found</h3>
            <p className="text-gray-500">Upload your first AC unit photo to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Model</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Manufacturer</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Dimensions</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Customer</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Added</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUnits.map((unit) => (
                  <tr key={unit.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-800">{unit.model_number}</div>
                      {unit.unit_type && (
                        <div className="text-xs text-gray-500">{unit.unit_type}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-gray-700">{unit.manufacturer || '-'}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm">
                        {unit.height_inches && unit.width_inches ? (
                          <>
                            <div className="font-medium text-gray-800">H: {unit.height_inches}" × W: {unit.width_inches}"</div>
                            {unit.depth_inches && <div className="text-xs text-gray-500">D: {unit.depth_inches}"</div>}
                          </>
                        ) : unit.tonnage ? (
                          <div className="text-gray-600">{unit.tonnage} Ton</div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {unit.customer_name ? (
                        <div>
                          <div className="text-sm text-gray-700">{unit.customer_name}</div>
                          {unit.latitude && unit.longitude && (
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              GPS matched
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Not assigned</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getStatusColor(unit.processing_status)}`}>
                        {getStatusIcon(unit.processing_status)}
                        {unit.processing_status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-gray-600">
                        {new Date(unit.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => window.open(unit.image_path, '_blank')}
                          className="text-blue-600 hover:text-blue-800"
                          title="View image"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEditModal(unit)}
                          className="text-green-600 hover:text-green-800"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(unit.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Edit AC Unit</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetEditForm();
                }}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <ImageIcon className="w-4 h-4 inline mr-1" />
                  AC Unit Photo (Optional)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleEditImageChange}
                    className="hidden"
                    id="edit-image-upload"
                  />
                  <label
                    htmlFor="edit-image-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    {editForm.imagePreview ? (
                      <img
                        src={editForm.imagePreview}
                        alt="Preview"
                        className="max-h-48 rounded mb-2"
                      />
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">Click to upload AC unit label photo</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, HEIC up to 10MB</p>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Model Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Number *
                </label>
                <input
                  type="text"
                  name="model_number"
                  value={editForm.model_number}
                  onChange={handleEditInputChange}
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., GSXC180361"
                />
              </div>

              {/* Manufacturer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                <input
                  type="text"
                  name="manufacturer"
                  value={editForm.manufacturer}
                  onChange={handleEditInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Goodman, Carrier, Trane"
                />
              </div>

              {/* Specs Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tonnage</label>
                  <input
                    type="number"
                    step="0.5"
                    name="tonnage"
                    value={editForm.tonnage}
                    onChange={handleEditInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="3.0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BTU</label>
                  <input
                    type="number"
                    name="btu"
                    value={editForm.btu}
                    onChange={handleEditInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="36000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SEER</label>
                  <input
                    type="number"
                    name="seer"
                    value={editForm.seer}
                    onChange={handleEditInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="16"
                  />
                </div>
              </div>

              {/* Dimensions Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Height (in)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="height_inches"
                    value={editForm.height_inches}
                    onChange={handleEditInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="36"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Width (in)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="width_inches"
                    value={editForm.width_inches}
                    onChange={handleEditInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="36"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Depth (in)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="depth_inches"
                    value={editForm.depth_inches}
                    onChange={handleEditInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="36"
                  />
                </div>
              </div>

              {/* Unit Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Type</label>
                <select
                  name="unit_type"
                  value={editForm.unit_type}
                  onChange={handleEditInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select type...</option>
                  <option value="Split System">Split System</option>
                  <option value="Package Unit">Package Unit</option>
                  <option value="Heat Pump">Heat Pump</option>
                  <option value="Ductless Mini-Split">Ductless Mini-Split</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Customer Assignment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Assign to Customer (Optional)
                </label>
                <select
                  name="customer_id"
                  value={editForm.customer_id}
                  onChange={handleEditInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Select customer...</option>
                  {appointments && appointments.map((apt) => (
                    <option key={apt.id} value={apt.id}>
                      {apt.customer_name} - {apt.service_address}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={editForm.notes}
                  onChange={handleEditInputChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Additional information about this AC unit..."
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || !editForm.model_number}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium"
                >
                  {isSubmitting ? 'Updating...' : 'Update AC Unit'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    resetEditForm();
                  }}
                  disabled={isSubmitting}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Customer Selector Modal */}
      {showCustomerSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Multiple Customers Found
              </h3>
              <p className="text-gray-600 mb-6">
                Multiple customers were found within 150 feet of the GPS location. Please select the correct customer:
              </p>

              <div className="space-y-3">
                {customerMatches.map((customer, index) => (
                  <button
                    key={index}
                    onClick={() => handleCustomerSelection(customer.customer_id)}
                    className="w-full text-left bg-gray-50 hover:bg-green-50 border border-gray-200 hover:border-green-500 rounded-lg p-4 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-gray-600" />
                          <span className="font-semibold text-gray-800">{customer.customer_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <MapPin className="w-3 h-3" />
                          <span>{customer.service_address}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <Phone className="w-3 h-3 inline mr-1" />
                          {customer.customer_phone}
                        </div>
                      </div>
                      <div className="ml-4 text-right">
                        <div className="text-sm font-medium text-green-600">
                          {customer.distance_feet} feet
                        </div>
                        <div className="text-xs text-gray-500">
                          away
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => {
                  setShowCustomerSelector(false);
                  setCustomerMatches([]);
                  setPendingUnitId(null);
                }}
                className="mt-6 w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminACUnitsAuto;
